<template>
  <div class="grid-system" :style="computedStyles">
    <slot />
  </div>
</template>

<script>
/**
 * GridSystem Component
 *
 * A modern CSS Grid layout system to replace Vuetify's v-row/v-col
 *
 * Usage:
 * <grid-system :columns="12" :gap="16">
 *   <div class="span-6">Half width</div>
 *   <div class="span-6">Half width</div>
 * </grid-system>
 *
 * Responsive columns:
 * <grid-system :columns="{ xs: 2, sm: 3, md: 4, lg: 6 }" :gap="8">
 *   <div>Grid item</div>
 * </grid-system>
 */
export default {
  name: "GridSystem",
  props: {
    columns: {
      type: [Number, String, Object],
      default: 12,
    },
    gap: {
      type: [Number, String],
      default: 16,
    },
    rowGap: {
      type: [Number, String],
      default: null,
    },
    columnGap: {
      type: [Number, String],
      default: null,
    },
    alignItems: {
      type: String,
      default: "stretch",
    },
    justifyItems: {
      type: String,
      default: "stretch",
    },
  },
  data() {
    return {
      windowWidth: 0,
      resizeHandler: null,
    };
  },
  mounted() {
    this.windowWidth = window.innerWidth;
    this.resizeHandler = this.handleResize.bind(this);
    window.addEventListener("resize", this.resizeHandler);

    // Force update on mount to ensure correct initial columns
    this.$nextTick(() => {
      this.$forceUpdate();
    });
  },
  beforeDestroy() {
    if (this.resizeHandler) {
      window.removeEventListener("resize", this.resizeHandler);
    }
  },
  methods: {
    handleResize() {
      this.windowWidth = window.innerWidth;
    },
    formatGap(value) {
      if (typeof value === "number") {
        return `${value}px`;
      }
      return value;
    },
    getCurrentColumns() {
      if (typeof this.columns === "object" && !Array.isArray(this.columns)) {
        // Responsive columns
        const breakpoints = {
          xs: 0,
          sm: 576,
          md: 768,
          lg: 992,
          xl: 1200,
          xxl: 1400,
        };

        let currentCols = 1;

        // Find the appropriate column count based on current window width
        const sortedBreakpoints = Object.entries(breakpoints).sort(
          (a, b) => a[1] - b[1]
        );

        for (const [breakpoint, minWidth] of sortedBreakpoints) {
          if (
            this.columns[breakpoint] !== undefined &&
            this.windowWidth >= minWidth
          ) {
            currentCols = this.columns[breakpoint];
          }
        }

        return currentCols;
      } else {
        // Single column value
        return typeof this.columns === "string"
          ? parseInt(this.columns)
          : this.columns;
      }
    },
  },
  computed: {
    computedStyles() {
      const styles = {
        display: "grid",
        width: "100%",
        "grid-template-columns": `repeat(${this.getCurrentColumns()}, 1fr)`,
      };

      // Handle gap
      if (this.rowGap !== null || this.columnGap !== null) {
        if (this.rowGap !== null) {
          styles["row-gap"] = this.formatGap(this.rowGap);
        }
        if (this.columnGap !== null) {
          styles["column-gap"] = this.formatGap(this.columnGap);
        }
      } else {
        styles.gap = this.formatGap(this.gap);
      }

      // Handle alignment
      if (this.alignItems !== "stretch") {
        styles["align-items"] = this.alignItems;
      }
      if (this.justifyItems !== "stretch") {
        styles["justify-items"] = this.justifyItems;
      }

      return styles;
    },
  },
};
</script>

<style lang="scss">
@use "@/styles/GridSystem.scss";
</style>
